// ============================================================
// CLIENT COMMUNICATIONS TYPES
// ============================================================

/**
 * Type of communication (how the message was sent)
 */
export type CommunicationType = 'email' | 'text_sms' | 'other';

/**
 * Available tags for organizing communications
 */
export type CommunicationTag =
  | 'important'
  | 'agreement'
  | 'quote'
  | 'complaint'
  | 'follow_up'
  | 'payment'
  | 'scheduling'
  | 'change_order';

/**
 * Communication type options for UI selectors
 */
export const COMMUNICATION_TYPES: { value: CommunicationType; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'text_sms', label: 'Text/SMS' },
  { value: 'other', label: 'Other' },
];

/**
 * Tag options for UI selectors
 */
export const COMMUNICATION_TAGS: { value: CommunicationTag; label: string; color: string }[] = [
  { value: 'important', label: 'Important', color: 'bg-red-100 text-red-700' },
  { value: 'agreement', label: 'Agreement', color: 'bg-green-100 text-green-700' },
  { value: 'quote', label: 'Quote', color: 'bg-blue-100 text-blue-700' },
  { value: 'complaint', label: 'Complaint', color: 'bg-orange-100 text-orange-700' },
  { value: 'follow_up', label: 'Follow Up', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'payment', label: 'Payment', color: 'bg-purple-100 text-purple-700' },
  { value: 'scheduling', label: 'Scheduling', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'change_order', label: 'Change Order', color: 'bg-pink-100 text-pink-700' },
];

/**
 * Get tag configuration by value
 */
export function getTagConfig(tag: CommunicationTag) {
  return COMMUNICATION_TAGS.find((t) => t.value === tag);
}

/**
 * Base client communication record from the database
 */
export interface ClientCommunication {
  id: string;
  businessId: string;
  projectId: string;
  uploadedBy: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number | null;
  mimeType: string | null;
  communicationType: CommunicationType;
  tags: CommunicationTag[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Client communication from view with joined context
 */
export interface ClientCommunicationView extends ClientCommunication {
  projectName: string;
  projectStatus: string;
  uploadedByName: string | null;
}

// ============================================================
// UPLOAD TYPES
// ============================================================

/**
 * Status of a file during the upload process
 */
export type CommunicationUploadStatus =
  | 'pending'
  | 'ready'
  | 'uploading'
  | 'uploaded'
  | 'error';

/**
 * A communication file in the upload queue
 */
export interface CommunicationFile {
  id: string;
  file: File;
  preview: string | null;
  fileType: 'image' | 'pdf' | 'eml';
  status: CommunicationUploadStatus;
  error?: string;
  uploadProgress?: number;
  communicationType?: CommunicationType;
  tags?: CommunicationTag[];
  notes?: string;
}

/**
 * Parameters for uploading a communication
 */
export interface UploadCommunicationParams {
  file: File;
  businessId: string;
  projectId: string;
  uploadedBy: string;
  communicationType: CommunicationType;
  tags?: CommunicationTag[];
  notes?: string;
}

/**
 * Parameters for updating a communication
 */
export interface UpdateCommunicationParams {
  id: string;
  communicationType?: CommunicationType;
  tags?: CommunicationTag[];
  notes?: string;
}

/**
 * Filter options for querying communications
 */
export interface CommunicationFilters {
  projectId?: string;
  communicationType?: CommunicationType;
  tags?: CommunicationTag[];
}

// ============================================================
// MAPPING HELPERS
// ============================================================

/**
 * Map database row to ClientCommunication type
 */
export function mapCommunicationFromDb(row: Record<string, unknown>): ClientCommunication {
  return {
    id: row.id as string,
    businessId: row.business_id as string,
    projectId: row.project_id as string,
    uploadedBy: row.uploaded_by as string,
    fileName: row.file_name as string,
    filePath: row.file_path as string,
    fileSizeBytes: row.file_size_bytes as number | null,
    mimeType: row.mime_type as string | null,
    communicationType: row.communication_type as CommunicationType,
    tags: (row.tags as CommunicationTag[]) || [],
    notes: row.notes as string | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

/**
 * Map database row to ClientCommunicationView type
 */
export function mapCommunicationViewFromDb(row: Record<string, unknown>): ClientCommunicationView {
  return {
    ...mapCommunicationFromDb(row),
    projectName: row.project_name as string,
    projectStatus: row.project_status as string,
    uploadedByName: row.uploaded_by_name as string | null,
  };
}

// ============================================================
// UTILITY TYPES & FUNCTIONS
// ============================================================

/**
 * Supported file types for communication upload
 */
export const COMMUNICATION_ACCEPTED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'message/rfc822',    // EML files
  'application/pdf',
] as const;

/**
 * File type to accept string for input elements
 */
export const COMMUNICATION_ACCEPT_STRING =
  '.jpg,.jpeg,.png,.heic,.heif,.webp,.eml,.pdf,image/jpeg,image/png,image/heic,image/heif,image/webp,message/rfc822,application/pdf';

/**
 * Check if a file is a supported communication type
 */
export function isValidCommunicationType(file: File): boolean {
  // Check by mime type first
  if (COMMUNICATION_ACCEPTED_TYPES.includes(file.type as typeof COMMUNICATION_ACCEPTED_TYPES[number])) {
    return true;
  }
  // Fallback to extension check for EML files (some browsers don't set mime type)
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'eml') return true;
  return false;
}

/**
 * Determine the file type category
 */
export function getFileTypeCategory(file: File): 'image' | 'pdf' | 'eml' {
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type === 'message/rfc822' || file.name.toLowerCase().endsWith('.eml')) return 'eml';
  return 'image';
}

/**
 * Check if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf';
}

/**
 * Check if a file is an EML file
 */
export function isEmlFile(file: File): boolean {
  return file.type === 'message/rfc822' || file.name.toLowerCase().endsWith('.eml');
}

/**
 * Check if a file is an image
 */
export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * Generate a unique ID for a communication file in the upload queue
 */
export function generateCommunicationId(): string {
  return `comm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Communication type display configuration
 */
export const COMMUNICATION_TYPE_CONFIG: Record<CommunicationType, { label: string; icon: string }> = {
  email: { label: 'Email', icon: 'mail' },
  text_sms: { label: 'Text/SMS', icon: 'message-square' },
  other: { label: 'Other', icon: 'file' },
};
